import { useEffect, useRef, useState } from "react";
import { Container, Grid, Typography, FormControl, RadioGroup, FormControlLabel, Radio, Button } from "@mui/material";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import globals from "../../globals";
import TextFieldRedBackground from "../../components/text_filed_red_background";
import BlueButton from "../../components/blueButton";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const RequestParticipation = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [instagramUsername, setInstagramUsername] = useState('');
    const [selectedGrade, setSelectedGrade] = useState('');
    const [city, setCity] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileError, setFileError] = useState('');
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState('');
    const fileInputRef = useRef(null);

    const acceptedExtensions = ['pdf', 'doc', 'docx', 'txt'];

    function handleFileChange(event) {
        const selectedFile = event.target.files?.[0];

        if (!selectedFile) return;

        const fileExtension = selectedFile.name.split('.').pop()?.toLowerCase();

        if (!fileExtension || !acceptedExtensions.includes(fileExtension)) {
            setUploadedFile(null);
            setFileError('Позволени формати: PDF, DOC, DOCX, TXT.');
            event.target.value = '';
            return;
        }

        setUploadedFile(selectedFile);
        setFileError('');
    }

    function handlePickFile() {
        fileInputRef.current?.click();
    }

    useEffect(() => {
        if (!token) return;

        async function rerouteIfAlreadyParticipated() {
            try {
                const response = await fetch(`${globals.apiUrl}/competitions/participation`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) return;

                const data = await response.json();
                if ((data?.participations?.length || 0) > 0) navigate('/competition');
            } catch {
                // Intentionally ignore: form remains available if check fails.
            }
        }

        rerouteIfAlreadyParticipated();
    }, [token, navigate]);

    async function handleSubmitRequest() {
        if (isSubmitting) return;
        if (!token) {
            setSubmitError('Необходимо е да си вписан, за да изпратиш заявка.');
            setSubmitMessage('');
            return;
        }

        if (!instagramUsername || !selectedGrade || !city || !uploadedFile) {
            setSubmitError('Попълни всички полета и прикачи файл преди изпращане.');
            setSubmitMessage('');
            return;
        }

        const formData = new FormData();
        formData.append('instagramUsername', instagramUsername);
        formData.append('grade', selectedGrade);
        formData.append('city', city);
        formData.append('file', uploadedFile);

        setSubmitError('');
        setSubmitMessage('');
        setIsSubmitting(true);

        try {
            const response = await fetch(`${globals.apiUrl}/competitions/participation`, {
                method: 'POST',
                body: formData,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                let errorMessage = 'Възникна грешка при изпращането. Опитай отново.';

                try {
                    const errorData = await response.json();
                    if (errorData?.detail) errorMessage = errorData.detail;
                } catch {
                    // Ignore JSON parse errors and use fallback message.
                }

                throw new Error(errorMessage);
            }

            setSubmitMessage('Успешно изпратена заявка!');
            setInstagramUsername('');
            setSelectedGrade('');
            setCity('');
            setUploadedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (error) {
            setSubmitError(error.message || 'Възникна грешка при изпращането. Опитай отново.');
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Container>
            <Grid textAlign={'center'}>
                <Typography sx={{ color: globals.colors.lightRed, fontSize: 25, fontWeight: 'bold', mt: 3 }}>Заявка за участие в конкурс за стихотворение за 24 май „Кажи го в стих“</Typography>
            </Grid>

            <Grid container justifyContent={'center'} direction={'column'} textAlign={'center'}>
                <Grid item sx={{ mt: 5 }}>
                    <Typography sx={{ color: globals.colors.darkBlue, fontSize: 20, fontWeight: 'bold', mb: 2 }}>Username в Instagram</Typography>
                </Grid>
                <Grid item>
                    <TextFieldRedBackground
                        value={instagramUsername}
                        onChange={(event) => setInstagramUsername(event.target.value)}
                        size="small"
                        fullWidth={false}
                        sx={{ width: { xs: 280, sm: 360 } }}
                    />
                </Grid>
            </Grid>

            <Grid container justifyContent={'center'} direction={'column'} textAlign={'center'}>
                <Grid item sx={{ mt: 5 }}>
                    <Typography sx={{ color: globals.colors.darkBlue, fontSize: 20, fontWeight: 'bold' }}>Клас</Typography>
                </Grid>
                <Grid item>
                    <FormControl>
                        <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            value={selectedGrade}
                            onChange={(event) => setSelectedGrade(event.target.value)}
                            sx={{
                                '& .MuiRadio-root': {
                                    color: globals.colors.lightRed,
                                },
                                '& .MuiRadio-root.Mui-checked': {
                                    color: globals.colors.lightRed,
                                },
                            }}
                        >
                            <FormControlLabel value="eight" control={<Radio />} label="8." />
                            <FormControlLabel value="nine" control={<Radio />} label="9." />
                            <FormControlLabel value="ten" control={<Radio />} label="10." />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container justifyContent={'center'} direction={'column'} textAlign={'center'}>
                <Grid item sx={{ mt: 5 }}>
                    <Typography sx={{ color: globals.colors.darkBlue, fontSize: 20, fontWeight: 'bold', mb: 2 }}>Населено място</Typography>
                </Grid>
                <Grid item>
                    <TextFieldRedBackground
                        value={city}
                        onChange={(event) => setCity(event.target.value)}
                        size="small"
                        fullWidth={false}
                        sx={{ width: { xs: 280, sm: 360 } }}
                    />
                </Grid>
            </Grid>

            <Grid container justifyContent={'center'} direction={'column'} textAlign={'center'}>
                <Grid item sx={{mt:4}}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                    />
                    <Button
                        variant="contained"
                        onClick={handlePickFile}
                        startIcon={<UploadFileIcon />}
                        sx={{
                            backgroundColor: globals.colors.lightRed,
                            '&:hover': { backgroundColor: globals.colors.lightRed },
                        }}
                    >
                        {uploadedFile ? 'Смени файла' : 'Прикачи произведение'}
                    </Button>
                </Grid>
                <Grid item sx={{ mt: 1 }}>
                    <Typography sx={{ color: globals.colors.darkBlue, fontSize: 13 }}>
                        Позволени формати: PDF, DOC, DOCX, TXT.
                    </Typography>
                </Grid>
                {uploadedFile && (
                    <Grid item sx={{ mt: 2 }}>
                        <Typography sx={{ color: globals.colors.darkBlue, fontSize: 15, fontWeight: 'bold' }}>
                            Избран файл: {uploadedFile.name}
                        </Typography>
                    </Grid>
                )}
                {fileError && (
                    <Grid item sx={{ mt: 1 }}>
                        <Typography sx={{ color: globals.colors.lightRed, fontSize: 14, fontWeight: 'bold' }}>
                            {fileError}
                        </Typography>
                    </Grid>
                )}
            </Grid>

            <Grid container justifyContent={'center'} sx={{ mt: 4, mb: 1 }}>
                <Grid item>
                    <BlueButton
                        text={isSubmitting ? "Изпращане..." : "Изпрати заявката"}
                        onClick={handleSubmitRequest}
                        pX={5}
                    />
                </Grid>
                {submitMessage && (
                    <Grid item xs={12} sx={{ mt: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: globals.colors.darkBlue, fontSize: 14, fontWeight: 'bold' }}>
                            {submitMessage}
                        </Typography>
                    </Grid>
                )}
                {submitError && (
                    <Grid item xs={12} sx={{ mt: 1, textAlign: 'center' }}>
                        <Typography sx={{ color: globals.colors.lightRed, fontSize: 14, fontWeight: 'bold' }}>
                            {submitError}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
}

export default RequestParticipation